import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, GitMerge,
  FileText, Clock, CheckCircle2, XCircle, BookCheck, RotateCcw, Loader2,
} from 'lucide-react';

import { cardReconciliationApi } from '@/api/cardReconciliation.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { Spinner }                from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { cn }                     from '@/utils/cn';

import type {
  CardReconciliationListDto,
  CardReconciliationFilterDto,
} from '@/types/cardReconciliation.types';
import {
  CARD_RECON_STATUS_COLORS,
} from '@/types/cardReconciliation.types';

const statusNameToKey: Record<string, string> = {
  'Draft':       'Draft',
  'In Progress': 'InProgress',
  'Submitted':   'Submitted',
  'Approved':    'Approved',
  'Rejected':    'Rejected',
  'Posted':      'Posted',
  'Reversed':    'Reversed',
};

const StatusIcon = ({ name }: { name: string }) => {
  const props = { size: 14, className: 'shrink-0' };
  switch (name) {
    case 'Draft':       return <FileText    {...props} className={`${props.className} text-gray-500 dark:text-gray-400`} />;
    case 'In Progress': return <Loader2     {...props} className={`${props.className} text-blue-500`} />;
    case 'Submitted':   return <Clock       {...props} className={`${props.className} text-indigo-500`} />;
    case 'Approved':    return <CheckCircle2{...props} className={`${props.className} text-green-500`} />;
    case 'Posted':      return <BookCheck   {...props} className={`${props.className} text-purple-600 dark:text-purple-400`} />;
    case 'Rejected':    return <XCircle     {...props} className={`${props.className} text-red-500`} />;
    case 'Reversed':    return <RotateCcw   {...props} className={`${props.className} text-amber-500`} />;
    default:            return null;
  }
};

export const CardReconciliationListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate         = hasPermission(Permissions.CARD_RECON_CREATE);

  const [items,   setItems]   = useState<CardReconciliationListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [search,        setSearch]        = useState('');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [statusFilter] = useState<number | undefined>();

  const load = useCallback(async (filter: CardReconciliationFilterDto) => {
    setLoading(true);
    try {
      const res  = await cardReconciliationApi.getList(filter);
      const data = res.data?.data;
      if (data) {
        setItems(data.items ?? []);
        setTotal(data.totalCount ?? 0);
      }
    } catch (err) {
      console.error('Failed to load card reconciliations', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const buildFilter = useCallback((): CardReconciliationFilterDto => ({
    search:   search   || undefined,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
    status:   statusFilter,
    page,
    pageSize,
  }), [search, dateFrom, dateTo, statusFilter, page]);

  useEffect(() => { load(buildFilter()); }, [page, statusFilter]); // eslint-disable-line

  const handleSearch = () => {
    setPage(1);
    load({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, status: statusFilter, page: 1, pageSize });
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages = Math.ceil(total / pageSize);

  // Status summary counts
  const statusCounts = {
    Draft:      items.filter(i => i.statusName === 'Draft').length,
    InProgress: items.filter(i => i.statusName === 'In Progress').length,
    Submitted:  items.filter(i => i.statusName === 'Submitted').length,
    Approved:   items.filter(i => i.statusName === 'Approved').length,
    Posted:     items.filter(i => i.statusName === 'Posted').length,
    Rejected:   items.filter(i => i.statusName === 'Rejected').length,
  };

  const statusCards = [
    { label: 'Draft',      icon: <FileText     size={14} />, count: statusCounts.Draft,      color: 'text-slate-500 dark:text-gray-400',  bg: 'bg-slate-50 dark:bg-slate-700/40',   ring: 'ring-slate-200' },
    { label: 'In Progress',icon: <Loader2      size={14} />, count: statusCounts.InProgress, color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20',     ring: 'ring-blue-200'  },
    { label: 'Submitted',  icon: <Clock        size={14} />, count: statusCounts.Submitted,  color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', ring: 'ring-indigo-200'},
    { label: 'Approved',   icon: <CheckCircle2 size={14} />, count: statusCounts.Approved,   color: 'text-green-600 dark:text-green-400',  bg: 'bg-green-50 dark:bg-green-900/20',   ring: 'ring-green-200' },
    { label: 'Posted',     icon: <BookCheck    size={14} />, count: statusCounts.Posted,     color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20', ring: 'ring-purple-200'},
    { label: 'Rejected',   icon: <XCircle      size={14} />, count: statusCounts.Rejected,   color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',       ring: 'ring-red-200'   },
  ] as const;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Card Reconciliation</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} total records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(buildFilter())} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.CARD_RECONCILIATION_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Reconciliation
              </button>
            )}
          </div>
        </div>

        {/* Status summary bar */}
        <div className="grid grid-cols-6 gap-2 mt-4">
          {statusCards.map(stat => (
            <button
              key={stat.label}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left hover:ring-2 hover:shadow-sm',
                stat.bg, stat.ring,
              )}
            >
              <span className={stat.color}>{stat.icon}</span>
              <div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{stat.label}</p>
                <p className={cn('text-sm font-bold leading-tight', stat.color)}>{stat.count}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search recon #, acquirer…"
          />
          <div className="flex items-center gap-2 ml-auto">
            <DateRangeFilter
              label="Sale date"
              from={dateFrom || undefined}
              to={dateTo || undefined}
              onChange={({ from, to }) => { setDateFrom(from ?? ''); setDateTo(to ?? ''); }}
            />
            <button onClick={handleSearch} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg">Search</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <GitMerge size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No card reconciliations found</p>
            {canCreate && (
              <button onClick={() => navigate(AppRoutes.CARD_RECONCILIATION_NEW)} className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
                Create first reconciliation
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Recon #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acquirer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Card Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date Range</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Expected Gross</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Difference</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(recon => {
                const statusKey = statusNameToKey[recon.statusName] ?? 'Draft';
                const statusColorClass = CARD_RECON_STATUS_COLORS[statusKey as keyof typeof CARD_RECON_STATUS_COLORS] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
                const isDiff = Math.abs(recon.differenceAmount) > 0.01;
                return (
                  <tr
                    key={recon.id}
                    onClick={() => navigate(AppRoutes.CARD_RECONCILIATION_DETAIL.replace(':id', recon.id))}
                    className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                      {recon.reconciliationNo}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-mono text-xs">{recon.financialPeriodCode}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{recon.acquirerName}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{recon.cardTypeName ?? 'All'}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs whitespace-nowrap">
                      {fmtDate(recon.dateFrom)} – {fmtDate(recon.dateTo)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {recon.expectedGrossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={cn('px-4 py-3 text-right font-mono font-semibold', isDiff ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400')}>
                      {isDiff ? '±' : ''}{Math.abs(recon.differenceAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <StatusIcon name={recon.statusName} />
                        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', statusColorClass)}>
                          {recon.statusName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(recon.createdDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={setPage}
          noun="reconciliations"
        />
      )}
    </div>
  );
};
